import React, { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Paper,
  Link,
  useTheme,
  Box,
  Button,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { InfoOutlined, Lock, Telegram as TelegramIcon, CheckCircleOutline } from "@mui/icons-material";
import { hasPaidAccess } from "../../../utils/access";
import {
  getLiveRoomReleaseDate,
  isLiveRoomReleased,
} from "../../../utils/liveRoomAccess";

const SALA_GROUP_SHOTS = [
  { src: "/sala-ao-vivo/grupo-1.jpg?v=3", alt: "Print da sala ao vivo com a turma operando" },
  { src: "/sala-ao-vivo/grupo-2.jpg?v=3", alt: "Print da sala ao vivo com resultados da turma" },
  { src: "/sala-ao-vivo/grupo-3.jpg?v=3", alt: "Print da sala ao vivo com leitura de mercado" },
  { src: "/sala-ao-vivo/grupo-4.jpg?v=3", alt: "Print da sala ao vivo com acompanhamento do dia" },
];

function LiveRoomGroupPreview({ images }) {
  const [index, setIndex] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    const loaders = images.map(
      (image) =>
        new Promise((resolve) => {
          const img = new window.Image();
          img.onload = resolve;
          img.onerror = resolve;
          img.src = image.src;
        })
    );

    Promise.all(loaders).then(() => {
      if (alive) setReady(true);
    });

    return () => {
      alive = false;
    };
  }, [images]);

  useEffect(() => {
    if (!ready || images.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % images.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [ready, images.length]);

  return (
    <Box sx={{ mt: 1.6, display: "flex", justifyContent: "center" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 340,
          position: "relative",
          borderRadius: "30px",
          p: "2px",
          overflow: "hidden",
          isolation: "isolate",
          bgcolor: "#0b121a",
          boxShadow: "0 0 24px rgba(42,171,238,0.18), 0 18px 40px rgba(0,0,0,0.4)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "180%",
            height: "180%",
            background:
              "conic-gradient(from 0deg, #0b121a 0 70%, rgba(42,171,238,0.25) 76%, #55dff7 84%, #FFD700 90%, #55dff7 95%, #0b121a 100%)",
            transform: "translate(-50%, -50%)",
            animation: ready ? "salaLedSpin 2.6s linear infinite" : "none",
            zIndex: 0,
            pointerEvents: "none",
          },
          "@keyframes salaLedSpin": {
            from: {
              transform: "translate(-50%, -50%) rotate(0deg)",
            },
            to: {
              transform: "translate(-50%, -50%) rotate(360deg)",
            },
          },
        }}
      >
        <Box
          sx={{
            borderRadius: "28px",
            overflow: "hidden",
            bgcolor: "#17212b",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.1,
              px: 1.4,
              py: 1.05,
              bgcolor: "#232e3c",
              borderBottom: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(42,171,238,0.18)",
                border: "1px solid rgba(42,171,238,0.28)",
                flexShrink: 0,
              }}
            >
              <TelegramIcon sx={{ color: "#2AABEE", fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, textAlign: "left" }}>
              <Typography sx={{ color: "#fff", fontSize: "0.84rem", fontWeight: 750, lineHeight: 1.15 }}>
                SALA AO VIVO
              </Typography>
              <Typography sx={{ color: "rgba(180,220,255,0.75)", fontSize: "0.68rem", mt: 0.15 }}>
                acompanhando o mercado agora
              </Typography>
            </Box>
            <Box
              sx={{
                px: 0.9,
                py: 0.35,
                borderRadius: 999,
                bgcolor: "rgba(255,64,110,0.14)",
                border: "1px solid rgba(255,64,110,0.28)",
              }}
            >
              <Typography sx={{ color: "#ffb3c4", fontSize: "0.62rem", fontWeight: 800 }}>
                AO VIVO
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: { xs: 420, sm: 460 },
              bgcolor: "#0e1621",
              overflow: "hidden",
            }}
          >
            {images.map((image, imageIndex) => (
              <Box
                key={image.src}
                component="img"
                src={image.src}
                alt={image.alt}
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center top",
                  display: "block",
                  opacity: ready && imageIndex === index ? 1 : 0,
                  transition: "opacity .35s ease",
                  pointerEvents: "none",
                  willChange: "opacity",
                }}
              />
            ))}

            {!ready && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "#0e1621",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <Typography sx={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem" }}>
                  Carregando sala...
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.55,
              px: 1.3,
              py: 1,
              bgcolor: "#1c2733",
            }}
          >
            {images.map((image, dotIndex) => (
              <Box
                key={image.src}
                component="button"
                type="button"
                aria-label={`Ver print ${dotIndex + 1}`}
                onClick={() => ready && setIndex(dotIndex)}
                sx={{
                  width: dotIndex === index ? 12 : 6,
                  height: 6,
                  border: 0,
                  p: 0,
                  borderRadius: 999,
                  cursor: ready ? "pointer" : "default",
                  bgcolor: dotIndex === index ? "#2AABEE" : "rgba(255,255,255,0.25)",
                  transition: "all .2s ease",
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Instructions({ switchTab, isPaid, user }) {
  const theme = useTheme();
  const [queueChecked, setQueueChecked] = useState(false);

  let salaLiberada = false;
  let dataLiberacaoFormatada = "";
  if (hasPaidAccess(user?.isPaid) && user?.paidAt) {
    const dataLiberacao = getLiveRoomReleaseDate(user.paidAt);
    if (dataLiberacao) {
      salaLiberada = isLiveRoomReleased({
        isPaid: user?.isPaid,
        paidAt: user?.paidAt,
      });
      dataLiberacaoFormatada = dataLiberacao.toLocaleDateString("pt-BR");
    }
  }

  const queueStorageKey = useMemo(() => {
    if (!user?.id || !dataLiberacaoFormatada) return null;
    return `live-room-queue:${user.id}:${dataLiberacaoFormatada}`;
  }, [user?.id, dataLiberacaoFormatada]);

  useEffect(() => {
    if (salaLiberada || !queueStorageKey) {
      setQueueChecked(false);
      return;
    }

    setQueueChecked(localStorage.getItem(queueStorageKey) === "true");
  }, [salaLiberada, queueStorageKey]);

  const setupSteps = [
    {
      title: "Baixe o arquivo para importação",
      description: (
        <>
          Acesse a aba{" "}
          <Link
            component="button"
            variant="body2"
            onClick={() => switchTab(2)}
            sx={{ color: "primary.main", textDecoration: "none", fontWeight: 700 }}
          >
            Arquivos
          </Link>{" "}
          para fazer o download do arquivo mensal (.psf).
        </>
      ),
    },
    {
      title: "Abra o Profit e importe o arquivo",
      description:
        "No Profit, vá até o menu Estratégias, clique em Importar Estratégias e selecione o arquivo baixado.",
    },
    {
      title: "Siga o tutorial em vídeo para instalação",
      description: (
        <>
          Acesse a aba{" "}
          <Link
            component="button"
            variant="body2"
            onClick={() => switchTab(1)}
            sx={{ color: "primary.main", textDecoration: "none", fontWeight: 700 }}
          >
            Vídeos
          </Link>{" "}
          e assista ao tutorial Instalação Indicadores.
        </>
      ),
    },
    {
      title: "Conheça os Indicadores FuturePro",
      description: "Assista ao vídeo Indicadores FuturePro na aba Vídeos.",
    },
    {
      title: "Automatização de indicadores",
      description: "Assista ao vídeo Automação para configurar a automatização dos indicadores.",
    },
  ];

  return (
    <Paper
      sx={{
        p: { xs: 2.2, md: 3 },
        bgcolor: "rgba(15,22,33,0.94)",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.1)",
        boxShadow: "0 16px 40px rgba(0,0,0,0.32)",
        maxWidth: theme.breakpoints.values.md,
        margin: "0 auto",
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ textAlign: "center", fontWeight: 800, color: "#F4F9FF" }}>
        Guia de Configuração
      </Typography>
      <Stack spacing={1.2}>
        {setupSteps.map((step, index) => (
          <Box
            key={step.title}
            sx={{
              p: 1.45,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.09)",
              bgcolor: "rgba(255,255,255,0.03)",
              display: "flex",
              gap: 1.2,
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: "rgba(78,154,253,0.2)",
                border: "1px solid rgba(78,154,253,0.45)",
                color: "#BFD9FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.8rem",
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              {index + 1}
            </Box>
            <Box>
              <Typography sx={{ color: "#F4F9FF", fontWeight: 700, mb: 0.2 }}>{step.title}</Typography>
              <Typography sx={{ color: "rgba(231,240,250,0.76)", fontSize: "0.92rem" }}>{step.description}</Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 2, p: 1.4, borderRadius: 2, bgcolor: "rgba(93,208,109,0.1)", border: "1px solid rgba(93,208,109,0.25)" }}>
        <Typography sx={{ color: "#CBF6D2", display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
          <CheckCircleOutline fontSize="small" />
          Dica: siga a ordem dos passos para evitar falhas de importação.
        </Typography>
      </Box>

      {/* Sala ao Vivo - Design refinado que você aprovou */}
      <Box
        id="sala-ao-vivo-section"
        sx={{
          mt: 6,
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          background:
            "linear-gradient(135deg, rgba(13,18,23,0.92) 0%, rgba(10,14,18,0.92) 100%)",
          backdropFilter: "blur(10px)",
          border: "1px solid rgba(255, 215, 0, 0.08)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Efeito de glow sutil no topo */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at 50% -40%, rgba(255,215,0,0.06) 0%, transparent 60%)",
          }}
        />

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "16px",
                background: "linear-gradient(180deg, rgba(37,211,102,0.18) 0%, rgba(37,211,102,0.08) 100%)",
                border: "1px solid rgba(37,211,102,0.22)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
              }}
            >
              <TelegramIcon sx={{ color: "#2AABEE", fontSize: 28 }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.1} sx={{ mb: 0.75 }}>
                Sala ao Vivo
              </Typography>

              <Typography
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  px: 1.2,
                  py: 0.5,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.72)",
                  fontSize: "0.76rem",
                  fontWeight: 600,
                  letterSpacing: 0.2,
                }}
              >
                Canal oficial da sala
              </Typography>
            </Box>

            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                px: 1.25,
                py: 0.6,
                borderRadius: 999,
                bgcolor: "rgba(42,171,238,0.08)",
                border: "1px solid rgba(42,171,238,0.18)",
              }}
            >
              <Typography sx={{ color: "#9EDFFF", fontSize: "0.75rem", fontWeight: 700 }}>
                Ao vivo
              </Typography>
            </Box>
          </Box>

          {salaLiberada ? (
            <Box
              sx={{
                p: { xs: 1.5, md: 1.75 },
                borderRadius: 3,
                background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                href="https://t.me/WILLIAM_SALA_AO_VIVO"
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<TelegramIcon />}
                sx={{
                  py: 1.65,
                  borderRadius: 3,
                  background: "linear-gradient(90deg, #1D8AC7 0%, #2AABEE 100%)",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  color: "#061521",
                  boxShadow: "0 12px 30px rgba(42,171,238,0.22)",
                  "&:hover": {
                    background: "linear-gradient(90deg, #229FDB 0%, #3DBBFF 100%)",
                    boxShadow: "0 16px 38px rgba(42,171,238,0.3)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Entrar na sala
              </Button>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.5,
                  pt: 1.25,
                  borderTop: "1px solid rgba(255,255,255,0.07)",
                }}
              >
                <InfoOutlined sx={{ color: "#9EC5FE", fontSize: 16, flexShrink: 0 }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.68)",
                    lineHeight: 1.5,
                    fontSize: "0.88rem",
                  }}
                >
                  Contato exclusivo com o responsavel pela Sala ao Vivo. Demandas de suporte da plataforma devem ser
                  tratadas pelos canais oficiais de atendimento.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Chip
                label="Sala cheia no momento"
                color="error"
                variant="outlined"
                size="small"
                icon={<Lock fontSize="small" />}
                sx={{ mb: 1.2, fontWeight: 600, borderColor: "rgba(255,46,99,0.5)" }}
              />

              <Typography
                sx={{
                  mb: 1.1,
                  px: { xs: 0.25, sm: 1 },
                  color: "rgba(231,240,250,0.86)",
                  fontSize: "0.9rem",
                  lineHeight: 1.5,
                }}
              >
                A turma de agora já começou. Sua vaga fica na próxima abertura.
              </Typography>

              <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.76rem" }}>
                Liberação prevista
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  mb: 1.6,
                  fontWeight: 800,
                  color: "#FFD700",
                  fontFamily: "monospace",
                  letterSpacing: 1.2,
                  fontSize: { xs: "1.45rem", sm: "1.7rem" },
                }}
              >
                {dataLiberacaoFormatada || "--/--/----"}
              </Typography>

              <Box
                sx={{
                  p: { xs: 1.4, sm: 1.6 },
                  borderRadius: 3,
                  textAlign: "left",
                  bgcolor: queueChecked ? "rgba(37,211,102,0.12)" : "rgba(42,171,238,0.08)",
                  border: queueChecked
                    ? "1px solid rgba(37,211,102,0.3)"
                    : "1px solid rgba(42,171,238,0.28)",
                }}
              >
                <Typography sx={{ color: "#F4F9FF", fontWeight: 800, mb: 0.45, fontSize: "0.95rem" }}>
                  {queueChecked ? "Você já está na fila" : "Reserve sua vaga"}
                </Typography>

                <Typography
                  sx={{
                    color: queueChecked ? "rgba(205,248,220,0.95)" : "rgba(231,240,250,0.8)",
                    fontSize: "0.84rem",
                    lineHeight: 1.5,
                    mb: 1.1,
                  }}
                >
                  {queueChecked
                    ? `Combinado. Em ${dataLiberacaoFormatada || "breve"} a sala abre pra você.`
                    : "Confirma aqui pra garantir seu lugar na próxima turma."}
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={queueChecked}
                      onChange={(event) => {
                        const nextValue = event.target.checked;

                        if (!nextValue && queueChecked) {
                          const confirmed = window.confirm(
                            "Se desmarcar agora, você sai da fila da próxima turma. Depois precisa entrar de novo no fim da fila. Quer sair mesmo assim?"
                          );

                          if (!confirmed) {
                            return;
                          }
                        }

                        setQueueChecked(nextValue);

                        if (queueStorageKey) {
                          localStorage.setItem(queueStorageKey, String(nextValue));
                        }
                      }}
                      sx={{
                        color: "rgba(255,255,255,0.64)",
                        "&.Mui-checked": {
                          color: "#25D366",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: "rgba(255,255,255,0.92)", fontWeight: 650, fontSize: "0.88rem" }}>
                      {queueChecked ? "Vaga confirmada" : "Quero minha vaga na próxima turma"}
                    </Typography>
                  }
                  sx={{ m: 0, alignItems: "center" }}
                />
              </Box>

              <LiveRoomGroupPreview images={SALA_GROUP_SHOTS} />
            </Box>
          )}
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{ mt: theme.spacing(3), textAlign: "center", fontStyle: "italic", color: "text.secondary" }}
      >
        Recomendamos que todos os vídeos sejam assistidos para uma experiência completa e melhor performance no mercado.
      </Typography>
    </Paper>
  );
}

export default Instructions;
